// BeneAIConfirmationCard — BeneAI-02.
//
// The user-visible gate the runtime drops in front of every mutating
// tool. The card renders the model's proposed action, exposes a
// risk-level pill, and offers Confirm / Cancel buttons that resolve
// the confirmation through the BeneAI REST surface.

import { AlertOctagon, Layers, Sparkles } from "lucide-react";

import type {
  BeneAIPendingConfirmationBlock,
  BeneAIRiskLevel,
} from "../types";


interface BeneAIConfirmationCardProps {
  block: BeneAIPendingConfirmationBlock;
  /** Disable the buttons while the confirm / cancel network call is
   *  in flight, OR if the block has already been resolved (the
   *  message list re-renders from content_json so a resolved block
   *  arriving from the backend should render the buttons as
   *  disabled). */
  pending?: boolean;
  resolved?: boolean;
  onConfirm: (confirmationId: string) => void;
  onCancel: (confirmationId: string) => void;
}


const RISK_META: Record<
  BeneAIRiskLevel,
  { label: string; chip: string; iconClass: string; Icon: typeof Sparkles }
> = {
  normal: {
    label: "Normal",
    chip: "bg-recessed text-text-secondary",
    iconClass: "text-text-tertiary",
    Icon: Sparkles,
  },
  destructive: {
    label: "Destructive",
    chip: "bg-ember-tint text-ember-ink",
    iconClass: "stroke-ember",
    Icon: AlertOctagon,
  },
  bulk: {
    label: "Bulk",
    chip: "bg-gold-tint text-gold-ink",
    iconClass: "stroke-gold",
    Icon: Layers,
  },
};


export function BeneAIConfirmationCard({
  block, pending = false, resolved = false,
  onConfirm, onCancel,
}: BeneAIConfirmationCardProps) {
  const meta = RISK_META[block.risk_level] ?? RISK_META.normal;
  const disabled = pending || resolved;
  return (
    <div
      data-testid="beneai-confirmation"
      data-confirmation-id={block.confirmation_id}
      data-risk={block.risk_level}
      data-status={block.status}
      className="self-stretch rounded-md border border-border-subtle bg-raised px-3 py-3 text-sm"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <meta.Icon
            size={14} aria-hidden="true" className={meta.iconClass}
          />
          <span className="font-semibold text-text-primary">
            {humanize(block.tool_name)}
          </span>
        </div>
        <span
          className={[
            "rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide",
            meta.chip,
          ].join(" ")}
        >
          {meta.label}
        </span>
      </div>
      <div className="mt-1 text-text-secondary">
        {block.summary || `Confirm ${humanize(block.tool_name)}?`}
      </div>
      {block.target_kind && block.target_id && (
        <div
          data-testid="beneai-confirmation-target"
          className="mt-1 text-xs text-text-tertiary"
        >
          {block.target_kind} <span className="font-mono">{block.target_id}</span>
        </div>
      )}
      <ProposedChangesList input_json={block.input_json} />
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          data-testid="beneai-confirmation-cancel"
          aria-label="Cancel proposed action"
          disabled={disabled}
          onClick={() => onCancel(block.confirmation_id)}
          className="rounded-md border border-border-subtle bg-canvas px-3 py-1 text-xs font-medium text-text-primary transition-fast ease-out hover:bg-recessed disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="beneai-confirmation-confirm"
          aria-label="Confirm proposed action"
          disabled={disabled}
          onClick={() => onConfirm(block.confirmation_id)}
          className="rounded-md bg-teal px-3 py-1 text-xs font-medium text-text-inverse transition-fast ease-out hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Confirm
        </button>
      </div>
    </div>
  );
}


function ProposedChangesList({
  input_json,
}: { input_json: Record<string, unknown> }) {
  const entries = Object.entries(input_json ?? {}).slice(0, 6);
  if (entries.length === 0) return null;
  return (
    <ul
      data-testid="beneai-confirmation-changes"
      className="mt-2 list-none space-y-0.5 text-xs text-text-secondary"
    >
      {entries.map(([key, value]) => (
        <li key={key}>
          <span className="text-text-tertiary">{key}:</span>{" "}
          <span className="text-text-primary">{formatValue(value)}</span>
        </li>
      ))}
    </ul>
  );
}


function formatValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") {
    return v.length > 80 ? `${v.slice(0, 80)}…` : v;
  }
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    const json = JSON.stringify(v);
    return json.length > 80 ? `${json.slice(0, 80)}…` : json;
  } catch {
    return "[…]";
  }
}


function humanize(toolName: string): string {
  return toolName.replace(/_/g, " ");
}
